import React, { Suspense, lazy, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Layout from '@/components/Layout';
import ScrollToTop from '@/components/ScrollToTop';
import Hero from '@/components/sections/Hero';
import ValueModel from '@/components/sections/ValueModel';
import SectorSplit from '@/components/sections/SectorSplit';
import SocialProof from '@/components/sections/SocialProof';
import Contact from '@/components/sections/Contact';
import MetricsStrip from '@/components/sections/MetricsStrip';
import { NODES, AU_STATES, COUNTRIES_SERVED } from '@/constants/locations';
import { motion, useInView } from 'framer-motion';
import { ORGANIZATION_SCHEMA } from '@/constants/organizationSchema';

// Code-split routed pages — keeps the landing page (LCP-critical) eager
// while every other route ships its own chunk. This dramatically reduces
// initial JS payload and improves INP / hydration timings.
const About = lazy(() => import('@/components/pages/About'));
const ContactPage = lazy(() => import('@/components/pages/ContactPage'));
const ServicesPage = lazy(() => import('@/components/pages/ServicesPage'));
const EnterpriseModernisationPage = lazy(() =>
  import('@/components/pages/EnterpriseModernisationPage')
);
const VentureEngineeringPage = lazy(() =>
  import('@/components/pages/VentureEngineeringPage')
);
const BuildVsBuyPage = lazy(() => import('@/components/pages/BuildVsBuyPage'));
const AwsServicesPage = lazy(() => import('@/components/pages/AwsServicesPage'));
const IntelHubPage = lazy(() => import('@/components/pages/IntelHubPage'));
const DossierDetail = lazy(() => import('@/components/pages/DossierDetail'));
const Assessment = lazy(() => import('@/components/pages/Assessment'));
const AIMaturityFrameworkPage = lazy(() => import('@/components/pages/AIMaturityFrameworkPage'));
const PrivacyPolicyPage = lazy(() => import('@/components/pages/legal/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('@/components/pages/legal/TermsOfServicePage'));
const LegacyCaseRedirect = lazy(() => import('@/components/LegacyCaseRedirect'));
const NotFound = lazy(() => import('@/components/NotFound'));

// Minimal Suspense fallback — keeps Largest Contentful Paint smooth by
// matching the page background so users don't see a flash of white.
const RouteFallback = () => (
  <div
    aria-busy="true"
    aria-live="polite"
    data-testid="route-fallback"
    className="min-h-[60vh] bg-[#1A1D21]"
  />
);

// Scroll-triggered reveal wrapper.
// Each section fades in and lifts 40px as it enters the viewport.
// `once: true` means the animation plays only on the first entry.
const ScrollReveal = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.75, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      {children}
    </motion.div>
  );
};

function LandingPage() {
  const serviceSchema = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Enterprise Modernisation",
      "provider": { "@id": "https://ddconsult.com.au/#organization" },
      "description": "Legacy system remediation and compliance architecture for Australian Healthcare, Legal, and Non-Profit sectors.",
      "serviceType": "Cloud Architecture",
      "audience": {
        "@type": "Audience",
        "audienceType": "CIOs and Risk Managers"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Venture Product Engineering",
      "provider": { "@id": "https://ddconsult.com.au/#organization" },
      "description": "Rapid MVP development and Series A technical due diligence preparation for high-growth startups.",
      "serviceType": "Software Engineering",
      "audience": {
        "@type": "Audience",
        "audienceType": "Founders and VCs"
      }
    }
  ];

  return (
    <>
      <Helmet>
        <title>DD Consulting | Australian AI Governance & Strategic Venture Product Engineering</title>
        <meta name="description" content="Sydney-based consultancy aligning business strategy with technical reality. Specialising in AI Governance, Enterprise Modernisation, APRA CPS 234 compliance, and Venture Product Engineering across Australia." />
        <meta name="keywords" content="AI governance Sydney, enterprise modernisation Australia, APRA CPS 234, venture product engineering, data sovereignty Australia, strategic consulting Sydney" />
        <link rel="canonical" href="https://ddconsult.com.au/" />
        
        {/* Social preview (WhatsApp / LinkedIn / X). Image + site_name come from
            the site-wide defaults in Layout.jsx; only page-specific copy here. */}
        <meta property="og:title" content="DD Consulting | AI Governance & Strategic Engineering, Sydney" />
        <meta property="og:description" content="We align business strategy with technical reality. AI governance (ISO 42001), enterprise modernisation, AWS sovereign cloud and venture product engineering — Sydney HQ, Australia-wide, 7 global offices." />
        <meta property="og:url" content="https://ddconsult.com.au/" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="DD Consulting" />
        <meta property="og:locale" content="en_AU" />
        <meta property="og:image" content="https://ddconsult.com.au/og/dd-consulting-og.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="DD Consulting — We align business strategy with technical reality" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://ddconsult.com.au/og/dd-consulting-og.png" />
        <meta name="twitter:title" content="DD Consulting | AI Governance & Strategic Engineering, Sydney" />
        <meta name="twitter:description" content="We align business strategy with technical reality. AI governance, enterprise modernisation, sovereign cloud and venture product engineering." />
      </Helmet>
      {/* JSON-LD rendered outside <Helmet> — react-helmet-async has a known
          edge case where <script> JSX children can prevent sibling <link>/<meta>
          tags from flushing on initial hard load. Putting the schema directly
          into the body avoids it; crawlers still discover the structured data. */}
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
      <Hero />
      <MetricsStrip />
      <ScrollReveal><ValueModel /></ScrollReveal>
      <ScrollReveal><SectorSplit /></ScrollReveal>
      <ScrollReveal><SocialProof /></ScrollReveal>
      <ScrollReveal><Contact /></ScrollReveal>
    </>
  );
}

function App() {
  return (
    <Router>
      <ScrollToTop />
      <Layout>
        {/* Organization schema on every route (see constants/organizationSchema.js) */}
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(ORGANIZATION_SCHEMA) }}
        />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/services/aws" element={<AwsServicesPage />} />

            {/* Enterprise Modernisation */}
            <Route path="/enterprise-modernisation" element={<EnterpriseModernisationPage />} />
            {/* 301 Redirect from old URL */}
            <Route path="/regulated-ops" element={<Navigate to="/enterprise-modernisation" replace />} />

            {/* Venture Product Engineering */}
            <Route path="/venture-product-engineering" element={<VentureEngineeringPage />} />
            {/* 301 Redirect from old URL */}
            <Route path="/venture-engineering" element={<Navigate to="/venture-product-engineering" replace />} />

            <Route path="/strategy/build-vs-buy" element={<BuildVsBuyPage />} />

            {/* Intelligence Hub */}
            <Route path="/intelligence" element={<IntelHubPage />} />
            {/* Legacy case_NN / CASE-NN URLs resolve to the current slug via the sheet's ref_id */}
            <Route path="/intelligence/:id" element={<LegacyCaseRedirect fallback={<DossierDetail />} />} />
            {/* 301 Redirects from old URLs */}
            <Route path="/knowledge-grid" element={<Navigate to="/intelligence" replace />} />
            <Route path="/knowledge-grid/:id" element={<Navigate to="/intelligence/:id" replace />} />
            {/* 301 Redirect from original Intel Hub URL */}
            <Route path="/intel-hub" element={<Navigate to="/intelligence" replace />} />
            <Route path="/intel-hub/:id" element={<Navigate to="/intelligence/:id" replace />} />

            {/* AI & Digital Maturity Assessment */}
            <Route path="/ai-maturity-framework" element={<AIMaturityFrameworkPage />} />
            <Route path="/assessment" element={<Assessment />} />

            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            {/* /government was never built — retired into the Enterprise (public sector) path */}
            <Route path="/government" element={<Navigate to="/enterprise-modernisation" replace />} />
            <Route path="/404" element={<NotFound />} />
            <Route path="*" element={<Navigate to="/404" replace />} />
          </Routes>
        </Suspense>
      </Layout>
    </Router>
  );
}

export default App;
